<script setup lang="ts">
    import { InputRow, MultiCombobox } from '@/components/ui/form';
    import { useEmployeeData } from '@/composables/useEmployeeData';
    import { computed, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { SelectOption, UserGroup } from '@/types';

    const props = defineProps<{
        form: any;
    }>();

    const { t } = useI18n();

    const { employeesAndGroups, isPlaceholderDataEmployeesAndGroups } = useEmployeeData(
        props.form.company_id,
    );

    const keepers = computed(() => {
        return employeesAndGroups.value.keepers || [];
    });

    const userGroups = computed(() => {
        return employeesAndGroups.value.user_groups || [];
    });

    const employeesAndGroupsOptions = computed<SelectOption[]>(() => {
        const hasGroups = !!userGroups.value.length;
        const hasEmployees = !!keepers.value.length;

        const makeGroup = (
            labelKey: string,
            entries: any[],
            addon: string = '',
            prefix: string = '',
        ) => [
            { is_group: true, label: t(labelKey), value: '' },
            ...entries.map((obj) => ({
                value: prefix + obj.id,
                label: obj.name,
                addon,
            })),
        ];

        const options: SelectOption[] = [];
        if (hasGroups)
            options.push(...makeGroup('user-groups.groups', userGroups.value, 'userGroup', 'ug-'));
        if (hasGroups && hasEmployees) options.push({ is_seperator: true, value: '', label: '' });
        if (hasEmployees) options.push(...makeGroup('user-groups.employees', keepers.value));

        return options;
    });

    watch(
        () => props.form.user_ids,
        (newVal, oldVal) => {
            if (!oldVal) return;

            const oldSelectedGroups = oldVal.filter((id: string) => id.startsWith('ug-'));
            const selectedGroups = newVal.filter((id: string) => id.startsWith('ug-'));
            const userIds = newVal.filter((id: string) => !id.startsWith('ug-'));

            const groupUserIds = selectedGroups.flatMap((groupId: string) => {
                const group = userGroups.value.find(
                    (g: UserGroup) => g.id.toString() === groupId.replace('ug-', ''),
                );
                return group ? group.usersIds.map((uid: number[]) => uid.toString()) : [];
            });

            const oldGroupUserIds = oldSelectedGroups.flatMap((groupId: string) => {
                const group = userGroups.value.find(
                    (g: UserGroup) => g.id.toString() === groupId.replace('ug-', ''),
                );
                return group ? group.usersIds.map((uid: number[]) => uid.toString()) : [];
            });

            const currentUserIds: string[] = [
                ...userIds,
                ...groupUserIds.filter((id: string) => !oldGroupUserIds.includes(id)),
            ].filter((id) => !oldGroupUserIds.includes(id) || groupUserIds.includes(id));

            const finalSet = new Set([...selectedGroups, ...currentUserIds]);

            const updated = Array.from(finalSet);

            if (JSON.stringify(updated) !== JSON.stringify(newVal)) {
                props.form.user_ids = updated;
            }
        },
        { deep: true },
    );
</script>

<template>
    <span tabindex="0" />

    <InputRow
        :form="form"
        name="user_ids"
        label="companies.user_companies_edit"
    >
        <MultiCombobox
            v-model="form.user_ids"
            :options="employeesAndGroupsOptions"
            :translate="false"
            :placeholder="t('companies.user_companies_edit')"
            :loading="isPlaceholderDataEmployeesAndGroups"
            :show-addon="false"
        />
    </InputRow>
</template>
